import { createContext, FC, useState } from 'react';
import { ICameraVideoTrack, IMicrophoneAudioTrack } from 'agora-rtc-sdk-ng';

import { LoggerService, RTCService } from 'services';
import { useModal } from 'hooks';
import { PermissionModal } from 'components';

export type BackstageProviderProps = {
  defaultAudioMuted?: boolean;
  defaultVideoMuted?: boolean;
  onAudioMutedChanged?(muted: boolean): void;
  onVideoMutedChanged?(muted: boolean): void;
};

export type BackstageState = {
  audio: {
    loading: boolean;
    muted: boolean;
    track: IMicrophoneAudioTrack | null;
  };
  video: {
    loading: boolean;
    muted: boolean;
    track: ICameraVideoTrack | null;
  };
};

export type BackStageContext = BackstageState & {
  toggleAudio?(): void;
  toggleVideo?(): void;
};

export const backstageContext = createContext<BackStageContext>({
  audio: {
    muted: true,
    loading: false,
    track: null
  },
  video: {
    muted: true,
    loading: false,
    track: null
  }
});

const BackstageProvider: FC<BackstageProviderProps> = ({
  children,
  defaultAudioMuted = true,
  defaultVideoMuted = true,
  onAudioMutedChanged,
  onVideoMutedChanged
}): JSX.Element => {
  const permissionModal = useModal();

  const [state, setState] = useState<BackstageState>({
    audio: {
      muted: defaultAudioMuted,
      loading: false,
      track: RTCService.microphoneTrack
    },
    video: {
      muted: defaultVideoMuted,
      loading: false,
      track: RTCService.cameraTrack
    }
  });

  const onPermissionDenied = () => permissionModal.open();

  const toggleAudio = async () => {
    setState(prev => ({ ...prev, audio: { ...prev.audio, loading: true } }));

    try {
      let { track } = state.audio;

      if (!track) {
        track = await RTCService.createMicrophoneTrack();
      }

      await track.setMuted(!state.audio.muted);

      setState(prev => ({ ...prev, audio: { ...prev.audio, muted: !prev.audio.muted, track } }));
      onAudioMutedChanged?.(!state.audio.muted);
    } catch (error) {
      LoggerService.log('TOGGLE_AUDIO', error);

      if (error) {
        // TODO: define missing type definitions for agora
        // @ts-ignore
        if (error.code === 'PERMISSION_DENIED') {
          onPermissionDenied?.();
        }
      }
    } finally {
      setState(prev => ({ ...prev, audio: { ...prev.audio, loading: false } }));
    }
  };

  const toggleVideo = async () => {
    setState(prev => ({ ...prev, video: { ...prev.video, loading: true } }));

    try {
      let { track } = state.video;

      if (!track) {
        track = await RTCService.createCameraTrack();
      }

      await track.setEnabled(state.video.muted);

      setState(prev => ({ ...prev, video: { ...prev.video, muted: !prev.video.muted, track } }));
      onVideoMutedChanged?.(!state.video.muted);
    } catch (error) {
      LoggerService.log('TOGGLE_VIDEO', error);

      if (error) {
        // TODO: define missing type definitions for agora
        // @ts-ignore
        if (error.code === 'PERMISSION_DENIED') {
          onPermissionDenied?.();
        }
      }
    } finally {
      setState(prev => ({ ...prev, video: { ...prev.video, loading: false } }));
    }
  };

  return (
    <backstageContext.Provider value={{ ...state, toggleAudio, toggleVideo }}>
      {permissionModal.isOpen && <PermissionModal onClose={permissionModal.close} />}
      {children}
    </backstageContext.Provider>
  );
};

export default BackstageProvider;
